import * as _firebase from "firebase/app";
import "firebase/auth";

const firebase = _firebase.default;

type AuthenticatedUser = {
  email?: string;
  // username?: string;
  id: string;
  guest?: boolean;
  facebook?: {
    id?: string | number;
    photoUrl?: string;
    displayName?: string;
  };
};

export type Logger = {
  log(msg: string): void;
  warn(msg: string): void;
  error(msg: string): void;
};

export type Storage = {
  getItem<T = void>(key: string): Promise<T | null>;
  setItem<T = void>(key: string, value: T): Promise<T>;
};

export default class Auth {
  private ready: Promise<void>;
  private user: AuthenticatedUser | null;

  private facebookLinkCredentials: { email: string; token: string } | null;

  constructor(private logger: Logger, private onUserChanged?: () => void) {
    this.user = null;
    this.facebookLinkCredentials = null;
    this.ready = this.init();
  }

  private async init() {
    await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    //
    // TODO: remove this unused code.
    // Check redirect result for Google login (on small screens).
    try {
      await firebase.auth().getRedirectResult();
    } catch (e) {
      console.error(e as string);
    }
    //
    let initializing = true;
    await new Promise<void>(resolve => {
      firebase.auth().onIdTokenChanged(user => {
        this.logger.log("idTokenChanged " + user?.uid);
        this.user = this.createAuthenticatedUserObject(user);
        if (this.onUserChanged) {
          this.onUserChanged();
        }
        if (initializing) {
          initializing = false;
          resolve();
        }
      });
    });
  }

  validateEmail(email: string): string | null {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const valid = re.test(email);
    if (!valid) {
      return "Invalid email";
    }
    return null;
  }

  validatePassword(password: string): string | null {
    if (password.length < 6) {
      return "Password too short";
    }
    return null;
  }

  signInWithGoogle = async (): Promise<Readonly<AuthenticatedUser> | null> => {
    await this.ready;
    var provider = new firebase.auth.GoogleAuthProvider();
    // provider.addScope('https://www.googleapis.com/auth/contacts.readonly');
    firebase.auth().useDeviceLanguage();
    if (window.innerWidth <= 768) {
      firebase.auth().signInWithRedirect(provider);
      return null;
    } else {
      await firebase.auth().signInWithPopup(provider);
      const user = await this.getCurrentUser();
      if (!user) {
        throw new Error("SignIn with Google failed");
      }
      return user;
    }
  };

  linkWithFacebook = async (password: string) => {
    await this.ready;
    if (!this.facebookLinkCredentials) {
      throw new Error("Could not link Facebook account.");
    }
    const { email, token } = this.facebookLinkCredentials;
    this.logger.log(`login ${email} and link with Facebook`);
    const firebaseUser = await firebase
      .auth()
      .signInWithEmailAndPassword(email, password);
    const credential = firebase.auth.FacebookAuthProvider.credential(token);
    await firebaseUser.user?.linkWithCredential(credential);
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("Login failed");
    }
    this.facebookLinkCredentials = null;
    return user;
  };

  continueWithFacebook = async () => {
    await this.ready;
    const provider = new firebase.auth.FacebookAuthProvider();
    provider.addScope("public_profile,email");
    firebase.auth().useDeviceLanguage();
    try {
      await firebase.auth().signInWithPopup(provider);
    } catch (e) {
      if ((e as any).code === "auth/account-exists-with-different-credential") {
        const token = (e as any).credential.accessToken;
        const email = (e as any).email;
        this.facebookLinkCredentials = { email, token };
        throw new Error("email already registered");
      } else {
        throw e;
      }
    }
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("SignIn with Facebook failed");
    }
    return user;
  };

  continueWithFacebookToken = async (
    token: string
  ): Promise<Readonly<AuthenticatedUser>> => {
    const credential = firebase.auth.FacebookAuthProvider.credential(token);
    // Sign in with the credential from the Facebook user.
    try {
      await firebase.auth().signInWithCredential(credential);
    } catch (e) {
      if ((e as any).code === "auth/account-exists-with-different-credential") {
        const token = (e as any).credential.accessToken;
        const email = (e as any).email;
        this.facebookLinkCredentials = { email, token };
        throw new Error("email already registered");
      } else {
        throw e;
      }
    }
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("SignIn with Facebook failed");
    }
    return user;
  };

  loginWithCustomToken = async (
    token: string
  ): Promise<Readonly<AuthenticatedUser>> => {
    await this.ready;
    this.logger.log(`loginWithCustomToken ${token}`);
    await firebase.auth().signInWithCustomToken(token);
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("SignIn with custom token failed");
    }
    return user;
  };

  login = async (
    email: string,
    password: string,
    remember: boolean
  ): Promise<Readonly<AuthenticatedUser>> => {
    await this.ready;
    this.logger.log(`login ${email}`);
    await firebase.auth().signInWithEmailAndPassword(email, password);
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("SignIn failed");
    }
    return user;
  };

  logout = async (): Promise<void> => {
    await this.ready;
    await firebase.auth().signOut();
    this.user = null;
  };

  recover = async (email: string): Promise<void> => {
    this.logger.log(`recover ${email}`);
    await this.ready;
    await firebase.auth().sendPasswordResetEmail(email);
  };

  signup = async (
    email: string,
    password: string
  ): Promise<Readonly<AuthenticatedUser>> => {
    await this.ready;
    this.logger.log(`signup ${email}`);
    await firebase.auth().createUserWithEmailAndPassword(email, password);
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("SignUp failed");
    }
    return user;
  };

  guestSignup = async (): Promise<Readonly<AuthenticatedUser>> => {
    await this.ready;
    this.logger.log(`guest signup`);
    await firebase.auth().signInAnonymously();
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error("Guest SignUp failed");
    }
    return user;
  };

  guestLink = async (
    email: string,
    password: string
  ): Promise<AuthenticatedUser> => {
    const credential = firebase.auth.EmailAuthProvider.credential(
      email,
      password
    );
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error("Guest Link failed: no current user");
    }
    const linkResult = await currentUser.linkWithCredential(credential);
    this.logger.log(`Guest ${linkResult?.user?.uid} link ok to ${email}`);
    const result = await this.getCurrentUser();
    if (!result) {
      throw new Error("Guest SignUp failed");
    }
    return result;
  };

  getCurrentUser = async (): Promise<Readonly<AuthenticatedUser> | null> => {
    await this.ready;
    const user = firebase.auth().currentUser;
    this.user = this.createAuthenticatedUserObject(user);
    return this.user;
  };

  private createAuthenticatedUserObject(
    user: _firebase.default.User | null
  ): AuthenticatedUser | null {
    if (!user) {
      return null;
    }
    const email = user.email || "unknown email";
    const anonymous = user.isAnonymous;
    const data: AuthenticatedUser = {
      id: user.uid,
      email,
      guest: anonymous
    };
    for (const providerData of user.providerData) {
      if (providerData?.providerId === "facebook.com") {
        data.facebook = {
          id: providerData.uid,
          photoUrl: providerData.photoURL || undefined,
          displayName: providerData.displayName || undefined
        };
      }
    }
    return data;
  }
}
